
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { ApparelProduct } from "@/types/models";
import ApparelService from "@/services/apparelService";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Card, CardContent } from "@/components/ui/card";
import { getCurrentOrgId } from "@/lib/auth";
import { CategoryField } from "./form/CategoryField";
import { InventoryFields } from "./form/InventoryFields";
import { ProductNameField } from "./form/ProductNameField";
import { DescriptionField } from "./form/DescriptionField";
import { PriceField } from "./form/PriceField";
import { ImageField } from "./form/ImageField";
import { StatusField } from "./form/StatusField";
import { SizeField } from "./form/SizeField";

const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  description: z.string().optional(),
  category: z.string().min(1, { message: "Category is required" }),
  price_cents: z.coerce.number().min(0, { message: "Price must be a positive number" }),
  has_inventory: z.boolean().default(true),
  inventory_count: z.coerce.number().optional(),
  requires_size: z.boolean().default(false),
  sizes: z.string().optional(),
  image_url: z.string().optional(),
  status: z.enum(["active", "inactive", "archived"]).default("active"),
});

interface ApparelProductFormProps {
  product?: ApparelProduct;
  categories?: string[];
  onSuccess?: (product: ApparelProduct) => void;
  onCancel?: () => void;
}

const ApparelProductForm: React.FC<ApparelProductFormProps> = ({
  product,
  categories = [],
  onSuccess,
  onCancel
}) => {
  const [loading, setLoading] = useState(false);
  const [orgId, setOrgId] = useState<string | null>(null);
  
  useEffect(() => {
    const getOrgId = async () => {
      const id = await getCurrentOrgId();
      setOrgId(id);
    };
    getOrgId();
  }, []);

  const defaultValues = {
    name: product?.name || "",
    description: product?.description || "",
    category: product?.category || (categories.length > 0 ? categories[0] : ""),
    price_cents: product?.price_cents ? product.price_cents / 100 : 0,
    has_inventory: product?.has_inventory ?? true,
    inventory_count: product?.inventory_count || 0,
    requires_size: product?.requires_size ?? false,
    sizes: product?.sizes ? JSON.stringify(product.sizes) : "",
    image_url: product?.image_url || "",
    status: (product?.status as "active" | "inactive" | "archived") || "active",
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const watchHasInventory = form.watch("has_inventory");
  const watchRequiresSize = form.watch("requires_size");

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!orgId) {
      toast.error("Organization ID not available");
      return;
    }
    
    try {
      setLoading(true);
      
      const priceCents = Math.round(values.price_cents * 100);
      
      let sizesObject = undefined;
      if (values.sizes) {
        try {
          sizesObject = JSON.parse(values.sizes);
        } catch (error) {
          sizesObject = values.sizes.split(",").map(s => s.trim());
        }
      }
      
      const productData = {
        name: values.name,
        description: values.description,
        organization_id: product?.organization_id || orgId,
        category: values.category, // This now handles both existing and new categories
        price_cents: priceCents,
        has_inventory: values.has_inventory,
        inventory_count: values.has_inventory ? values.inventory_count : null,
        requires_size: values.requires_size,
        sizes: values.requires_size ? sizesObject : null,
        image_url: values.image_url || null,
        status: values.status,
      };
      
      let result;
      
      if (product?.id) {
        result = await ApparelService.updateProduct(product.id, productData);
        if (result) {
          toast.success("Product updated successfully");
        }
      } else {
        result = await ApparelService.createProduct(productData);
        if (result) {
          toast.success("Product created successfully");
          form.reset();
        }
      }
      
      if (result && onSuccess) {
        onSuccess(result);
      }
    } catch (error) {
      console.error("Error saving product:", error);
      toast.error("Failed to save product");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <ProductNameField form={form} />
            <DescriptionField form={form} />
            <CategoryField
              form={form}
              categories={categories}
            />
            <PriceField form={form} />
            <ImageField form={form} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InventoryFields form={form} watchHasInventory={watchHasInventory} />
              {watchHasInventory && (
                <SizeField form={form} watchRequiresSize={watchRequiresSize} />
              )}
            </div>
            <StatusField form={form} />
            <div className="flex justify-end space-x-2 pt-4">
              {onCancel && (
                <Button type="button" variant="outline" onClick={onCancel}>
                  Cancel
                </Button>
              )}
              <Button type="submit" disabled={loading}>
                {loading ? "Saving..." : product?.id ? "Update Product" : "Create Product"}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default ApparelProductForm;
